import { Entity, ProppingSystem } from '@inter-ikea-core/core';
import { Product, ProductDesign } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { useAppDispatch } from 'ducks/hooks';
import {
  filterApplied,
  getFilterState,
  getMarketProducts,
  getProductGroups,
  getProductsForGroup,
  getSelectedGroupId
} from 'ducks/products';
import * as R from 'ramda';
import {
  getMajorityColor,
  isEntitiesReplaceable,
  isProductOfEquivalentColor
} from 'rangeConfig/shared/utils/colorUtils/utils';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ColorPicker from './ColorPicker';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { useIntl } from 'react-intl';
import { isMobile } from 'utils/platform';
import { isProppingVisible } from 'ducks/layout';
import { useBehaviorTracker } from 'contexts/behaviors';

const getProductDesigns = (products: Product[]) => {
  const designs = products
    .map(product => product.design.split('/')[0])
    .filter(design => design !== ProductDesign.DARK_GREY_METAL);
  return R.uniq(designs);
};

type ChangeColorProps = {
  onChangeColor: (design: ProductDesign) => void;
  entities: Entity[];
  defaultColor: ProductDesign;
};

const ChangeColor = ({ onChangeColor, entities, defaultColor }: ChangeColorProps) => {
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const behaviorTracker = useBehaviorTracker();
  const products = useSelector(getMarketProducts);
  const stateProductGroups = useSelector(getProductGroups);
  const selectedGroupId = useSelector(getSelectedGroupId);
  const productsInGroup = useSelector(getProductsForGroup);
  const filterState = useSelector(getFilterState);
  const colorFilterState = filterState(selectedGroupId, 'design');
  const { formatMessage } = useIntl();
  const proppingVisible = useSelector(isProppingVisible);

  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();

  const [currentColor, setCurrentColor] = useState(defaultColor);
  const selectedGroup = R.find(R.propEq('id', selectedGroupId), productGroups);

  const designsInGroup = getProductDesigns(productsInGroup).filter(Boolean) as ProductDesign[];

  const onColorChanged = useCallback(
    (design: ProductDesign, listIndex: number) => {
      const meta = { color: design, listIndex };
      behaviorTracker.withMeta(meta).custom(async () => {
        if (isEntitiesReplaceable(design, entities, products)) {
          await coreApi.performLongCoreOperation(() => {
            coreApi.validations['WallVisibilityValidation']?.disable();
            coreApi.validations['SeeThroughFramesValidation']?.disable();
            proppingVisible && coreApi.getSystem<ProppingSystem>('ProppingSystem')?.disable();
            onChangeColor(design);
            setCurrentColor(design);
            coreApi.validations['WallVisibilityValidation']?.enable();
            coreApi.validations['SeeThroughFramesValidation']?.enable();
            proppingVisible && coreApi.getSystem<ProppingSystem>('ProppingSystem')?.enable();
          });
        } else {
          setCurrentColor(design);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [behaviorTracker, coreApi, entities, onChangeColor, products]
  );

  const filterProducts = useCallback(
    (design: ProductDesign) => {
      if (selectedGroup) {
        if (currentColor !== design) {
          setCurrentColor(design);
        }
        if (colorFilterState.design !== design) {
          const newFilterState = { ...colorFilterState, design };
          const filteredProducts = productsInGroup
            .filter(p => isProductOfEquivalentColor(p, design))
            .map(p => p.coreProductId);
          dispatch(
            filterApplied({
              id: 'design',
              productGroupId: selectedGroup.id,
              productIdList: filteredProducts,
              filterState: newFilterState
            })
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentColor, dispatch, productsInGroup, selectedGroup, selectedGroupId]
  );

  const onUpdate = useCallback(() => {
    if (isEntitiesReplaceable(currentColor, entities, products)) {
      const color = getMajorityColor(entities, designsInGroup) || currentColor;
      filterProducts(color);
    } else {
      filterProducts(currentColor);
    }
  }, [currentColor, designsInGroup, entities, filterProducts, products]);

  useEffect(() => {
    return coreApi.subscribe('load', onUpdate);
  });

  useEffect(() => {
    if (Object.keys(stateProductGroups).length > 0) {
      onUpdate();
    }
  }, [onUpdate, stateProductGroups]);

  // We need to show the color in mobile even if there is only one choice or we will show an empty drawer.
  return designsInGroup.length > 1 || isMobile() ? (
    <>
      {!isMobile() && (
        <Title type={ThemeFontStyleTypeEnum.headingSmall} pb="xxs">
          {formatMessage({ id: 'storageone.changeColorLabel' })}
        </Title>
      )}
      <ColorPicker
        colors={designsInGroup}
        currentColor={currentColor}
        onChangeColor={onColorChanged}
      />
    </>
  ) : (
    <></>
  );
};

export default ChangeColor;
